import { z } from "zod"
import {
  documentClassificationStatusEnum,
  documentStatusEnum,
  documentTypeEnum,
} from "../../../db/schema/document"
import { zAddress } from "../contact/types"

// Create zod schemas from pgEnum (source of truth)
export const documentTypeSchema = z.enum(documentTypeEnum.enumValues)
export type DocumentType = z.infer<typeof documentTypeSchema>

export const documentStatusSchema = z.enum(documentStatusEnum.enumValues)
export type DocumentStatus = z.infer<typeof documentStatusSchema>

export const documentClassificationStatusSchema = z.enum(
  documentClassificationStatusEnum.enumValues,
)
export type DocumentClassificationStatus = z.infer<typeof documentClassificationStatusSchema>

const LOCATION_OR_ADDRESS_MUTUAL_EXCLUSION_MESSAGE =
  "Cannot provide both locationId and address. Please provide only one."

/**
 * Document upload forms use this select option value for inline address creation (see
 * `use-document-upload-address-fields`). It must be stripped before UUID validation and must not
 * count as a real `locationId` for mutual exclusion with `address`.
 */
export const DOCUMENT_UPLOAD_CREATE_NEW_LOCATION_ID = "__create_new__" as const

function isConcreteDocumentLocationId(locationId: unknown): locationId is string {
  if (typeof locationId !== "string" || locationId.length === 0) return false
  if (locationId === DOCUMENT_UPLOAD_CREATE_NEW_LOCATION_ID) return false
  return true
}

/** Accepts a UUID, null/undefined, or the create-new sentinel (coerced to undefined). */
export const zDocumentLocationIdInput = z.preprocess(
  (val: unknown) => (val === DOCUMENT_UPLOAD_CREATE_NEW_LOCATION_ID ? undefined : val),
  z.string().uuid().nullish(),
)

/**
 * Use with `.superRefine()` so validation errors attach to `locationId` and `address` separately
 * (`.refine({ path: ["locationId", "address"] })` would incorrectly target a nested path).
 */
export function refineLocationIdAndAddressMutualExclusion(
  data: { locationId?: string | null; address?: unknown },
  ctx: z.RefinementCtx,
): void {
  if (isConcreteDocumentLocationId(data.locationId) && data.address != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: LOCATION_OR_ADDRESS_MUTUAL_EXCLUSION_MESSAGE,
      path: ["locationId"],
    })
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: LOCATION_OR_ADDRESS_MUTUAL_EXCLUSION_MESSAGE,
      path: ["address"],
    })
  }
}

/** Base fields for add-document; extend this when composing variants (e.g. trust) before applying {@link refineLocationIdAndAddressMutualExclusion}. */
export const addDocumentInputBaseSchema = z.object({
  estateId: z.string().uuid(),
  type: documentTypeSchema,
  title: z.string().min(1),
  description: z.string().nullish(),
  fileName: z.string().min(1),
  contactId: z.string().uuid().nullish(),
  assetId: z.string().uuid().nullish(),
  locationId: zDocumentLocationIdInput,
  address: zAddress.optional(),
})

export const AddDocumentInput = addDocumentInputBaseSchema.superRefine(
  refineLocationIdAndAddressMutualExclusion,
)

export type AddDocumentInput = z.infer<typeof AddDocumentInput>

export const DeleteDocumentInput = z.object({
  documentId: z.string().uuid(),
})

export type DeleteDocumentInput = z.infer<typeof DeleteDocumentInput>

export const GetDocumentsByAssetInput = z.object({
  assetId: z.string().uuid(),
})

export type GetDocumentsByAssetInput = z.infer<typeof GetDocumentsByAssetInput>

export const ClassifyDocumentInput = z.object({
  documentId: z.string().uuid(),
})

export type ClassifyDocumentInput = z.infer<typeof ClassifyDocumentInput>

// Classification runs asynchronously; the call returns the request status and
// clients poll `getClassification` for the result.
export const ClassifyDocumentOutput = z.object({
  status: documentClassificationStatusSchema,
})

export type ClassifyDocumentOutput = z.infer<typeof ClassifyDocumentOutput>

export const GetClassificationInput = z.object({
  documentId: z.string().uuid(),
})

export type GetClassificationInput = z.infer<typeof GetClassificationInput>

export const GetClassificationOutput = z.object({
  status: documentClassificationStatusSchema.nullable(),
  classifiedType: documentTypeSchema.nullable(),
  confidence: z.number().nullable(),
  reasoning: z.string().nullable(),
  pagesUsed: z.number().nullable(),
  completedAt: z.date().nullable(),
})

export type GetClassificationOutput = z.infer<typeof GetClassificationOutput>

export const UpdateDocumentTypeInput = z.object({
  documentId: z.string().uuid(),
  documentType: documentTypeSchema,
})

export type UpdateDocumentTypeInput = z.infer<typeof UpdateDocumentTypeInput>

export const UpdateDocumentTypeOutput = z.object({
  documentId: z.string().uuid(),
  documentType: documentTypeSchema,
  workflowStarted: z.boolean(),
})

export type UpdateDocumentTypeOutput = z.infer<typeof UpdateDocumentTypeOutput>
